import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GroupedBarChart } from '@carbon/charts-react';
import { green, red, blue } from '@carbon/colors';
import { useMoney } from 'src/hooks/useMoney';
import { TransactionTypeEnum, TransactionTypes } from 'src/utils/accounting';
import { useChartTheme } from './useChartTheme';

interface SalesByMenuProps {
  title: string;
  transactionsByDateRange: any[];
  dateFormat: string;
}

const DailyTransactions = React.memo((props: SalesByMenuProps) => {
  const { t } = useTranslation('dashboard');
  const chartTheme = useChartTheme();
  const { format, formatCompact } = useMoney();
  const transformedTransactionTypes = TransactionTypes.reduce((previousObject, currentObject) => {
    return Object.assign(previousObject, {
      [currentObject.value]: t(`translation:${currentObject.label}`),
    });
  }, {});

  const getScaleOptions = () => {
    const options = {};
    props.transactionsByDateRange.map((item) => {
      if (
        !options[transformedTransactionTypes[item.transactionType]] &&
        item.transactionType === TransactionTypeEnum.INCOME
      ) {
        options[transformedTransactionTypes[TransactionTypeEnum.INCOME]] = green[50];
      }
      if (
        !options[transformedTransactionTypes[item.transactionType]] &&
        item.transactionType === TransactionTypeEnum.EXPENSE
      ) {
        options[transformedTransactionTypes[TransactionTypeEnum.EXPENSE]] = red[60];
      }
      if (
        !options[transformedTransactionTypes[item.transactionType]] &&
        item.transactionType === TransactionTypeEnum.INVESTMENT
      ) {
        options[transformedTransactionTypes[TransactionTypeEnum.INVESTMENT]] = blue[50];
      }
    });
    return options;
  };

  return (
    <div className="dashboard__stat_widget">
      <GroupedBarChart
        data={props.transactionsByDateRange.map((item) => ({
          group: transformedTransactionTypes[item.transactionType],
          date: item.bucketKey,
          value: item.transactionTotal,
        }))}
        options={{
          title: props.title,
          axes: {
            left: {
              mapsTo: 'value',
              ticks: { formatter: (value) => formatCompact(value) },
            },
            bottom: {
              mapsTo: 'date',
              scaleType: 'time' as any,
              ticks: {
                formatter: (date) => {
                  if (
                    dayjs(date).format(props.dateFormat) === dayjs().format(props.dateFormat) &&
                    props.dateFormat === 'DD/MM'
                  ) {
                    return `Today (${dayjs(date).format(props.dateFormat)})`;
                  }
                  return dayjs(date).format(props.dateFormat || 'DD/MM');
                },
              },
            },
          },
          color: {
            pairing: {
              option: 2,
            },
            scale: getScaleOptions(),
          },
          tooltip: { valueFormatter: (value) => format(value) },
          experimental: true,
          height: '400px',
          zoomBar: {
            top: {
              enabled: true,
              type: 'slider_view' as any,
              initialZoomDomain:
                props.dateFormat === 'MM/YYYY'
                  ? [dayjs().subtract(7, 'month').toDate(), dayjs().add(1, 'month').toDate()]
                  : [dayjs().subtract(7, 'day').toDate(), dayjs().add(1, 'day').toDate()],
            },
          },
          theme: chartTheme,
        }}
      />
    </div>
  );
});

export default DailyTransactions;
